import { FormattedMessage, useIntl } from 'react-intl'

import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'

import ShippingReturnRulesItem from '../../../components/ShippingReturnRulesItem'
import {
  ListingResolvedReturns,
  ListingReturnsInfo,
  ListingShippingScope
} from '../../helpers/shippingReturnRules.helpers'
import msg from '../ShippingReturnRules.messages'

interface ReturnsInfoBlockProps {
  returnsInfo: ListingResolvedReturns
}

interface ReturnsPolicyDetailsProps {
  returns: ListingReturnsInfo
  title?: string
}

const ReturnsPolicyDetails: RFC<ReturnsPolicyDetailsProps> = ({
  returns,
  title
}) => {
  const { formatMessage } = useIntl()
  const { returnPaidBy, returnDays, returnPolicyInfo } = returns

  const returnPaidByLabel =
    returnPaidBy === 'buyer' || returnPaidBy === 'seller'
      ? formatMessage(msg[returnPaidBy])
      : ''

  return (
    <Stack spacing={2}>
      {!!title && (
        <Text variant="body2" sx={{ fontWeight: 500 }}>
          {title}
        </Text>
      )}

      {!!returnPaidByLabel && (
        <ShippingReturnRulesItem
          title={formatMessage(msg.returnsPaidBy)}
          value={returnPaidByLabel}
        />
      )}

      <ShippingReturnRulesItem
        title={formatMessage(msg.returnsInitiated)}
        value={returnDays ? `${returnDays} days` : ''}
      />

      <ShippingReturnRulesItem
        title={formatMessage(msg.returnsRules)}
        value={returnPolicyInfo || ''}
      />
    </Stack>
  )
}

const ReturnsNotSupported: RFC<{ title?: string }> = ({ title }) => (
  <Stack spacing={1}>
    {!!title && (
      <Text variant="body2" sx={{ fontWeight: 500 }}>
        {title}
      </Text>
    )}

    <Text variant="body2" color="text.third">
      <FormattedMessage defaultMessage="Seller doesn’t accept returns." />
    </Text>
  </Stack>
)

const ReturnsInfoBlock: RFC<ReturnsInfoBlockProps> = ({ returnsInfo }) => {
  const { formatMessage } = useIntl()

  const policies = (
    [
      {
        scope: 'domestic' as const,
        returns: returnsInfo.domestic
      },
      {
        scope: 'international' as const,
        returns: returnsInfo.international
      }
    ] as const
  ).filter(
    (
      policy
    ): policy is {
      scope: ListingShippingScope
      returns: ListingReturnsInfo
    } => !!policy.returns
  )

  const showScopeLabels = policies.length > 1
  const hasAcceptedReturns = policies.some(
    (policy) => policy.returns.acceptReturn
  )

  const getScopeTitle = (scope: ListingShippingScope) =>
    formatMessage(
      scope === 'domestic' ? msg.domesticReturns : msg.internationalReturns
    )

  return (
    <Stack spacing={2} sx={{ marginTop: 3 }}>
      <Text variant="body2" sx={{ fontWeight: 500 }}>
        <FormattedMessage defaultMessage="Returns Info" />
      </Text>

      {!hasAcceptedReturns && !showScopeLabels ? (
        <Text variant="body2" color="text.third">
          <FormattedMessage defaultMessage="Seller doesn’t accept returns." />
        </Text>
      ) : (
        <Stack spacing={3}>
          {policies.map(({ scope, returns }) => {
            const title = showScopeLabels ? getScopeTitle(scope) : undefined

            if (!returns.acceptReturn) {
              return <ReturnsNotSupported key={scope} title={title} />
            }

            return (
              <ReturnsPolicyDetails
                key={scope}
                returns={returns}
                title={title}
              />
            )
          })}
        </Stack>
      )}
    </Stack>
  )
}

export default ReturnsInfoBlock
